class PremiumReels extends HTMLElement {
  connectedCallback() {
    this.events?.abort();
    this.track = this.querySelector('[data-reel-track]');
    this.viewport = this.querySelector('[data-reel-viewport]');
    if (!this.track || !this.viewport) return;
    this.querySelectorAll('[data-reel-clone]').forEach((card) => card.remove());
    this.originals = [...this.track.querySelectorAll('[data-reel-card]')];
    this.count = this.originals.length;
    if (!this.count) return;
    this.dataset.count = this.count;
    this.events = new AbortController();
    this.mobile = matchMedia('(max-width: 749px)');
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.index = Math.min(this.count - 1, this.mobile.matches ? 1 : 2);
    this.muted = true;
    this.userPaused = false;
    this.motionOptIn = false;
    this.visible = false;
    this.suspended = false;
    this.focused = false;
    this.moving = false;
    this.blocked = false;
    this.pending = new WeakSet();
    this.buffer = this.count === 1 ? 0 : Math.ceil(3 / this.count) * this.count;
    this.physical = this.buffer + this.index;
    this.originals.forEach((card, index) => { card.dataset.reelIndex = index; });
    const clone = (index) => {
      const card = this.originals[this.mod(index)].cloneNode(true);
      card.dataset.reelClone = '';
      card.removeAttribute('data-shopify-editor-block');
      card.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      return card;
    };
    const before = Array.from({ length: this.buffer }, (_, index) => clone(index - this.buffer));
    const after = Array.from({ length: this.buffer }, (_, index) => clone(index));
    this.track.prepend(...before);
    this.track.append(...after);
    this.cards = [...this.track.querySelectorAll('[data-reel-card]')];
    this.videos = this.cards.map((card) => card.querySelector('video'));
    this.videos.forEach((video) => {
      video.autoplay = false;
      video.removeAttribute('autoplay');
      video.controls = false;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.loop = true;
      video.pause();
    });
    const options = { signal: this.events.signal };
    this.addEventListener('click', this.handleClick, options);
    this.addEventListener('keydown', this.handleKeydown, options);
    this.addEventListener('focusin', () => { this.focused = true; this.clearTimer(); }, options);
    this.addEventListener('focusout', (event) => { this.focused = this.contains(event.relatedTarget); this.sync(); }, options);
    this.viewport.addEventListener('pointerdown', this.handlePointerDown, { ...options, passive: true });
    this.viewport.addEventListener('pointerup', this.handlePointerUp, options);
    this.viewport.addEventListener('pointercancel', () => { this.gesture = null; }, options);
    this.track.addEventListener('transitionend', (event) => {
      if (event.target === this.track && event.propertyName === 'transform') this.finishMove();
    }, options);
    document.addEventListener('visibilitychange', this.sync, options);
    window.addEventListener('pagehide', this.suspend, options);
    window.addEventListener('pageshow', () => { this.suspended = false; this.sync(); }, options);
    this.mobile.addEventListener('change', this.measure, options);
    this.motion.addEventListener('change', () => { this.motionOptIn = false; this.finishMove(); this.sync(); }, options);
    document.addEventListener('shopify:block:select', (event) => {
      const index = this.originals.findIndex((card) => card.dataset.blockId === event.detail?.blockId);
      if (index < 0) return;
      this.userPaused = true;
      this.goTo(index, { animate: false });
    }, options);
    this.resize = new ResizeObserver(this.measure);
    this.resize.observe(this.viewport);
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        this.visible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= .01);
        this.sync();
      }, { threshold: [0, .01] });
      this.observer.observe(this.viewport);
    } else {
      this.visible = true;
      window.addEventListener('scroll', this.sync, { ...options, passive: true });
      window.addEventListener('resize', this.measure, options);
    }
    this.measure();
    this.dataset.ready = 'true';
  }

  disconnectedCallback() {
    this.events?.abort();
    this.observer?.disconnect();
    this.resize?.disconnect();
    this.clearTimer();
    clearTimeout(this.moveTimer);
    this.pauseAll();
  }

  mod(index) { return ((index % this.count) + this.count) % this.count; }

  canRun() {
    const bounds = this.viewport.getBoundingClientRect();
    return this.isConnected && !this.suspended && this.visible && document.visibilityState === 'visible'
      && bounds.width > 0 && bounds.height > 0 && bounds.bottom > 0 && bounds.top < innerHeight
      && bounds.right > 0 && bounds.left < innerWidth;
  }

  automaticAllowed() { return !this.userPaused && (!this.motion.matches || this.motionOptIn); }

  clearTimer() { clearTimeout(this.slideTimer); this.slideTimer = null; }

  pauseAll(except) {
    this.videos?.forEach((video) => { if (video !== except) video.pause(); });
  }

  suspend = () => { this.suspended = true; this.clearTimer(); this.pauseAll(); };

  measure = () => {
    if (!this.isConnected || !this.cards?.length) return;
    this.clearTimer();
    clearTimeout(this.moveTimer);
    this.moving = false;
    this.physical = this.buffer + this.index;
    this.visibleCount = this.count === 1 ? 1 : this.mobile.matches ? 3 : 5;
    this.cardWidth = parseFloat(getComputedStyle(this.cards[0]).width);
    this.step = this.cardWidth + parseFloat(getComputedStyle(this.track).columnGap || 0);
    this.place(true);
    this.updateControls();
    this.sync();
  };

  place(instant = false) {
    if (instant) this.track.classList.add('is-resetting');
    const x = this.viewport.clientWidth / 2 - this.cardWidth / 2 - this.physical * this.step;
    this.track.style.transform = `translate3d(${x}px, 0, 0)`;
    if (instant) {
      this.track.getBoundingClientRect();
      this.track.classList.remove('is-resetting');
    }
  }

  updateControls() {
    const radius = Math.floor(this.visibleCount / 2);
    this.cards.forEach((card, position) => {
      const active = position === this.physical;
      const visible = Math.abs(position - this.physical) <= radius;
      const number = Number(card.dataset.reelIndex) + 1;
      card.classList.toggle('is-active', active);
      card.classList.toggle('is-paused', active && !this.automaticAllowed());
      card.classList.toggle('is-blocked', active && this.blocked);
      card.setAttribute('aria-hidden', String(!visible));
      card.inert = !visible;
      const select = card.querySelector('[data-reel-select]');
      select.tabIndex = visible ? 0 : -1;
      select.setAttribute('aria-label', `${active && this.automaticAllowed() && !this.blocked ? 'Pause' : 'Play'} reel ${number}`);
      const sound = card.querySelector('[data-reel-sound]');
      sound.tabIndex = active ? 0 : -1;
      sound.setAttribute('aria-label', `${this.muted ? 'Unmute' : 'Mute'} reel ${number}`);
      sound.setAttribute('aria-pressed', String(!this.muted));
    });
    this.querySelectorAll('[data-reel-dot]').forEach((dot) => {
      if (Number(dot.dataset.reelDot) === this.index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
    const paused = !this.automaticAllowed();
    const toggle = this.querySelector('[data-reels-toggle]');
    toggle?.setAttribute('aria-pressed', String(paused));
    toggle?.setAttribute('aria-label', paused ? 'Play reels' : 'Pause reels');
  }

  playActive() {
    const video = this.videos[this.physical];
    this.pauseAll(video);
    if (!video || !video.paused || this.pending.has(video) || this.blocked) return;
    video.muted = this.muted;
    this.pending.add(video);
    const stillActive = () => this.canRun() && this.automaticAllowed() && !this.moving && this.videos[this.physical] === video;
    Promise.resolve(video.play()).catch(async () => {
      if (!stillActive()) return;
      if (!video.muted) {
        this.muted = true;
        video.muted = true;
        this.updateControls();
        try { await video.play(); return; } catch { /* Show the native play affordance below. */ }
      }
      this.blocked = true;
      this.updateControls();
    }).finally(() => {
      this.pending.delete(video);
      if (!stillActive()) video.pause();
    });
  }

  sync = () => {
    if (!this.track || !this.cards?.length) return;
    if (!this.canRun()) {
      this.clearTimer();
      this.pauseAll();
      if (this.moving) this.finishMove();
      return;
    }
    this.updateControls();
    if (!this.automaticAllowed() || this.moving) {
      this.pauseAll();
      this.clearTimer();
      return;
    }
    this.playActive();
    if (this.count > 1 && !this.focused && !this.slideTimer) {
      this.slideTimer = setTimeout(() => {
        this.slideTimer = null;
        if (this.canRun() && this.automaticAllowed()) this.goTo(this.index + 1, { direction: 1 });
      }, Math.max(3000, Number(this.dataset.interval) || 5000));
    }
  };

  goTo(index, { direction, animate = true, manual = false } = {}) {
    if (this.moving) this.finishMove();
    this.clearTimer();
    this.pauseAll();
    const next = this.mod(index);
    let delta = next - this.index;
    if (direction === 1) delta = 1;
    else if (direction === -1) delta = -1;
    else if (delta > this.count / 2) delta -= this.count;
    else if (delta < -this.count / 2) delta += this.count;
    if (manual) { this.userPaused = false; this.motionOptIn = true; }
    this.blocked = false;
    this.index = next;
    this.physical += delta;
    this.moving = animate && !!delta && this.canRun() && !this.motion.matches;
    this.updateControls();
    this.place(!this.moving);
    if (manual) this.querySelector('[data-reel-status]').textContent = `Reel ${this.index + 1} of ${this.count}`;
    if (this.moving) this.moveTimer = setTimeout(() => this.finishMove(), 650);
    else this.finishMove();
  }

  finishMove() {
    clearTimeout(this.moveTimer);
    const focusedCard = this.track?.contains(document.activeElement) && document.activeElement.closest('[data-reel-card]');
    this.moving = false;
    this.physical = this.buffer + this.index;
    if (!this.cards?.length) return;
    this.place(true);
    this.updateControls();
    if (focusedCard && !this.cards[this.physical].contains(document.activeElement)) {
      this.cards[this.physical].querySelector('[data-reel-select]').focus({ preventScroll: true });
    }
    this.sync();
  }

  handleClick = (event) => {
    if (this.suppressClickUntil > performance.now() && event.target.closest('[data-reel-select]')) { event.preventDefault(); return; }
    const dot = event.target.closest('[data-reel-dot]');
    if (dot) { this.goTo(Number(dot.dataset.reelDot), { manual: true }); return; }
    if (event.target.closest('[data-reels-toggle]')) {
      this.userPaused = this.automaticAllowed();
      this.motionOptIn = !this.userPaused;
      this.blocked = false;
      this.sync();
      return;
    }
    const card = event.target.closest('[data-reel-card]');
    if (!card || !this.contains(card)) return;
    if (event.target.closest('[data-reel-sound]')) {
      if (Number(card.dataset.reelIndex) !== this.index) this.goTo(Number(card.dataset.reelIndex), { animate: false, manual: true });
      this.muted = !this.muted;
      this.videos[this.physical].muted = this.muted;
      this.blocked = false;
      this.sync();
      return;
    }
    if (event.target.closest('[data-reel-select]')) {
      if (card !== this.cards[this.physical]) this.goTo(Number(card.dataset.reelIndex), { manual: true });
      else {
        this.userPaused = this.automaticAllowed() && !this.blocked;
        this.motionOptIn = !this.userPaused;
        this.blocked = false;
        this.sync();
      }
    }
  };

  handleKeydown = (event) => {
    if (!event.target.closest('[data-reel-select], [data-reel-dot]') || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const index = event.key === 'Home' ? 0 : event.key === 'End' ? this.count - 1 : this.index + (event.key === 'ArrowRight' ? 1 : -1);
    this.goTo(index, { manual: true });
    this.querySelector(`[data-reel-dot="${this.index}"]`)?.focus({ preventScroll: true });
  };

  handlePointerDown = (event) => {
    if (event.pointerType === 'mouse' || event.target.closest('[data-reel-sound]')) return;
    this.gesture = { x: event.clientX, y: event.clientY };
  };

  handlePointerUp = (event) => {
    if (!this.gesture) return;
    const dx = event.clientX - this.gesture.x;
    const dy = event.clientY - this.gesture.y;
    this.gesture = null;
    if (Math.abs(dx) < 30 || Math.abs(dx) < Math.abs(dy) * 1.3) return;
    this.suppressClickUntil = performance.now() + 400;
    this.goTo(this.index + (dx < 0 ? 1 : -1), { direction: dx < 0 ? 1 : -1, manual: true });
  };
}

if (!customElements.get('premium-reels')) customElements.define('premium-reels', PremiumReels);
